import React, { createContext, useContext, ReactNode } from 'react';
import { useFirestoreWithAudit } from './useFirestoreWithAudit';
import { firestoreModular } from '../firebaseModular';
import { QuerySnapshot, DocumentData, Firestore } from 'firebase/firestore';

// Interfaz para nuestro contexto de auditoría de Firestore
interface FirestoreAuditContextType {
  firestore: Firestore;
  getDoc: (collectionPath: string, docId: string, sectionName: string) => Promise<any>;
  addDoc: (collectionPath: string, data: DocumentData, sectionName: string) => Promise<any>;
  updateDoc: (collectionPath: string, docId: string, data: DocumentData, sectionName: string) => Promise<boolean>;
  deleteDoc: (collectionPath: string, docId: string, sectionName: string) => Promise<boolean>;
  onSnapshot: (
    collectionPath: string, 
    sectionName: string, 
    callback: (snapshot: QuerySnapshot<DocumentData>) => void, 
    options?: any
  ) => () => void;
}

// Creamos el contexto
const FirestoreAuditContext = createContext<FirestoreAuditContextType | undefined>(undefined);

// Props para nuestro proveedor
interface FirestoreAuditProviderProps {
  children: ReactNode;
}

/**
 * Proveedor para operaciones de Firestore con auditoría
 * 
 * Este componente proporciona métodos simples para operaciones de Firestore
 * que automáticamente registran la actividad en el sistema de auditoría.
 */
export const FirestoreAuditProvider: React.FC<FirestoreAuditProviderProps> = ({ children }) => {
  const {
    getDocWithAudit,
    addDocWithAudit,
    updateDocWithAudit,
    deleteDocWithAudit,
    onSnapshotWithAudit
  } = useFirestoreWithAudit();

  // Simplificamos las firmas de los métodos para los consumidores
  const getDoc = (collectionPath: string, docId: string, sectionName: string) => {
    return getDocWithAudit(firestoreModular, collectionPath, docId, sectionName);
  };

  const addDoc = (collectionPath: string, data: DocumentData, sectionName: string) => {
    return addDocWithAudit(firestoreModular, collectionPath, data, sectionName);
  };

  const updateDoc = (collectionPath: string, docId: string, data: DocumentData, sectionName: string) => {
    return updateDocWithAudit(firestoreModular, collectionPath, docId, data, sectionName);
  };

  const deleteDoc = (collectionPath: string, docId: string, sectionName: string) => {
    return deleteDocWithAudit(firestoreModular, collectionPath, docId, sectionName);
  };

  const onSnapshot = (
    collectionPath: string, 
    sectionName: string, 
    callback: (snapshot: QuerySnapshot<DocumentData>) => void, 
    options?: any
  ) => {
    return onSnapshotWithAudit(firestoreModular, collectionPath, sectionName, callback, options);
  };

  return (
    <FirestoreAuditContext.Provider
      value={{
        firestore: firestoreModular,
        getDoc,
        addDoc,
        updateDoc,
        deleteDoc,
        onSnapshot
      }}
    >
      {children}
    </FirestoreAuditContext.Provider>
  );
};

/**
 * Hook para usar Firestore con auditoría
 */
export const useFirestoreAudit = (): FirestoreAuditContextType => {
  const context = useContext(FirestoreAuditContext);
  
  if (context === undefined) {
    throw new Error('useFirestoreAudit debe usarse dentro de un FirestoreAuditProvider');
  }
  
  return context;
};
